import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Tag, Switch, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, GiftOutlined } from '@ant-design/icons';
import { PostBotin, DeleteBotin } from '../../../dao/jutge/BotinesDao';
import BotinRecompensesModal from '../botines/BotinRecompensesModal';
import BotinBattleConfig from '../botines/BotinBattleConfig';

interface TabBotinesProps {
    isAdmin: boolean;
    loading: boolean;
    botines: any[];
    fetchBotines: () => Promise<void>;
}

export const TabBotines: React.FC<TabBotinesProps> = ({
    isAdmin,
    loading,
    botines,
    fetchBotines
}) => {
    const [botinModal, setBotinModal] = useState(false);
    const [botinRecompensesModal, setBotinRecompensesModal] = useState(false);
    const [selectedBotin, setSelectedBotin] = useState<any>(null);
    const [editingItem, setEditingItem] = useState<any>(null);
    const [form] = Form.useForm();

    const handleSaveBotin = async (values: any) => {
        try {
            const body = {
                ...values,
                id: editingItem?.id || 0,
                active: values.active ? 1 : 0
            };
            const res = await PostBotin(body);
            if (!res || res.status !== 'ok') throw new Error('Error');
            message.success("Botín guardado");
            setBotinModal(false);
            fetchBotines();
        } catch (e) {
            message.error("Error al guardar el botín");
        }
    };

    const handleDeleteBotin = async (id: number) => {
        try {
            const ok = await DeleteBotin(id);
            if (!ok) throw new Error('Error');
            message.success("Botín eliminado");
            fetchBotines();
        } catch (e) {
            message.error("Error al eliminar el botín");
        }
    };

    const columnsBotin = [
        { title: 'ID', dataIndex: 'id', key: 'id' },
        { title: 'Identificador', dataIndex: 'identifier', key: 'identifier', render: (v: string) => <Tag color="geekblue">{v}</Tag> },
        { title: 'Nombre', dataIndex: 'nom', key: 'nom' },
        { title: 'Tipo', dataIndex: 'tipo', key: 'tipo', render: (v: string) => v === 'batalla' ? <Tag color="volcano">⚔️ Batalla</Tag> : <Tag>Normal</Tag> },
        { title: 'Descripción', dataIndex: 'descr', key: 'descr' },
        { title: 'Recompensas', dataIndex: 'num_recompenses', key: 'num_recompenses', render: (v: number) => <b>{v || 0}</b> },
        {
            title: 'Reclamado por',
            key: 'num_usuaris_reclamats',
            render: (_: any, record: any) => {
                const count = record.num_usuaris_reclamats ?? record.num_total_reclamos ?? 0;
                return (
                    <Tag color={count > 0 ? "blue" : "default"} style={{ fontWeight: 600 }}>
                        👤 {count} {count === 1 ? 'alumno' : 'alumnos'}
                    </Tag>
                );
            }
        },
        { title: 'Activo', dataIndex: 'active', key: 'active', render: (val: number) => <Tag color={val ? 'green' : 'red'}>{val ? 'SÍ' : 'NO'}</Tag> },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_: any, record: any) => (
                <Space>
                    <Button type="primary" icon={<GiftOutlined />} onClick={() => { setSelectedBotin(record); setBotinRecompensesModal(true); }}>
                        Recompensas
                    </Button>
                    {isAdmin && (
                        <>
                            <Button icon={<EditOutlined />} onClick={() => { setEditingItem(record); form.setFieldsValue({ ...record, active: !!record.active }); setBotinModal(true); }} />
                            <Popconfirm title="¿Eliminar este botín?" onConfirm={() => handleDeleteBotin(record.id)} okText="Sí" cancelText="No">
                                <Button danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, padding: '12px', background: '#f0f2f5', borderRadius: '8px' }}>
                Crea botines y asígnales recompensas (puntos, skins, armas, magias, flechas, arcos, avatares, javamon aleatorio / específico / de temporada, etc.).
                En Unity, un objeto interactuable de tipo <b>Loot</b> solo necesita indicar el <b>identificador</b> del botín que se otorga al interactuar con él.
            </div>
            <Space style={{ marginBottom: 16 }}>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingItem(null);
                            form.resetFields();
                            form.setFieldsValue({ active: true });
                            setBotinModal(true);
                        }}
                    >
                        Nuevo Botín
                    </Button>
                )}
                <Button icon={<ReloadOutlined />} onClick={fetchBotines} loading={loading}>
                    Refrescar
                </Button>
            </Space>
            <Table dataSource={botines} columns={columnsBotin} loading={loading} rowKey="id" />

            {/* Modal Botín (crear/editar) */}
            <Modal
                title={editingItem ? "Editar Botín" : "Nuevo Botín"}
                open={botinModal}
                onCancel={() => setBotinModal(false)}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSaveBotin} initialValues={{ active: true }}>
                    <Form.Item
                        name="identifier"
                        label="Identificador (el que seleccionas en Unity)"
                        rules={[{ required: true, message: 'Identificador obligatorio' }]}
                        tooltip="Identificador único (aparte del id autoincremental). Es el que asignas al objeto Loot en Unity."
                    >
                        <Input placeholder="Ej: cofre_bosque_1, tesoro_final..." />
                    </Form.Item>
                    <Form.Item name="nom" label="Nombre" rules={[{ required: true, message: 'Nombre obligatorio' }]}>
                        <Input placeholder="Ej: Cofre del bosque" />
                    </Form.Item>
                    <Form.Item name="descr" label="Descripción">
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item name="active" label="Activo" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    {/* Tipo de botín + configuración de combate (solo si tipo = batalla) */}
                    <BotinBattleConfig form={form} />
                </Form>
            </Modal>

            {/* Modal recompensas de un botín */}
            {botinRecompensesModal && selectedBotin && (
                <BotinRecompensesModal
                    botin={selectedBotin}
                    visible={botinRecompensesModal}
                    isAdmin={isAdmin}
                    onClose={() => { setBotinRecompensesModal(false); setSelectedBotin(null); fetchBotines(); }}
                />
            )}
        </div>
    );
};
